/**
 * Content-based document auto-classifier — Doc_Classification_And_Evidence_Labels AC1.
 *
 * Why this exists:
 *   The evidence-readiness "Docs" component (`evidenceReadiness.ts`) scores a
 *   site by how many checklist items its uploaded documents satisfy. The
 *   matcher (`matchUploadedDocsToChecklist` in `documentChecklists.ts`) does a
 *   case-insensitive substring match over `fileName + tags + summary`. A
 *   user who uploads `Redhead_Industrial_2026_03_15_final_v2.pdf` (an 80-page
 *   Phase I ESA) with no tags and no summary contributes ZERO coverage — the
 *   filename never contains "phase i". The user stays at `Docs: 0%` no matter
 *   how many real documents they upload.
 *
 * Fix:
 *   When a doc is uploaded (and via the `npm run backfill:doc-tags` helper for
 *   pre-existing docs), run a cheap content classifier over the doc's
 *   `textContent` — the first few pages of extracted text — against the union
 *   of every report type's `relevanceTagPattern`. The matched patterns get
 *   merged into `doc.tags`. Downstream nothing changes: `matchUploadedDocsToChecklist`
 *   already reads `tags`, so once tags are populated the coverage is correct.
 *
 * Idempotency:
 *   Every classified doc carries `autoTaggedAt`. The on-upload hook and the
 *   backfill both skip docs that already have it. Manual tags are never
 *   overwritten — `mergeAutoTags` unions, it doesn't replace.
 *
 * Resilience:
 *   Gemini-first, then a deterministic substring fallback over the text body.
 *   The fallback is itself a strict improvement over the legacy filename-only
 *   match (it reads the document body), so a Gemini outage degrades precision,
 *   not correctness. Never throws to the caller — a classification failure
 *   leaves the doc untagged (recoverable later via the admin backfill).
 */
import { Type, runGeminiJSON } from './geminiClient';
import { documentBodyText } from '../lib/documentBody';
import { DOC_CHECKLISTS } from '../config/documentChecklists';
import {
  hasUsableExtractedText,
  looksLikeDocumentImageText,
} from './knowledge/extractedTextQuality';
import type { SiteDocument } from '../types/siteRag';

/** Stamped on every auto-classified doc so a future schema change can target old rows.
 *  PL4 — bumped to v2 so the admin backfill targets older rows that
 *  were tagged before the sales-deck / deal-packet / project-brief /
 *  one-pager gateway tags joined the closed vocabulary. */
// v3 (P3, 2026-09-07) — the classifier reads the content-index topic summaries
// plus the opening of the WHOLE body, not the first 4,000 chars of the preview.
export const AUTO_TAG_VERSION = '2026.09.07-doctag-v3';

/** Max characters of extracted text fed to the classifier (~first 2-3 pages). */
const MAX_CLASSIFY_CHARS = 6000;

/**
 * PL4 — intent-extractor activation tags. These are NOT checklist
 * relevanceTagPatterns; they're the gateway tags that
 * `extractIntentFromDocs` (lib/intentDocExtractor.ts) looks for. Mirror
 * `SALES_DECK_TAGS` exactly — without these, `extractIntentFromDocs`
 * never activates because the auto-classifier emits nothing matching.
 * Effect: any uploaded doc the classifier recognises as a sales deck /
 * deal packet / project brief / one-pager will populate the wizard's
 * Project Intent fields with SuggestionChips automatically.
 */
export const INTENT_GATEWAY_TAGS: readonly string[] = [
  'sales-deck',
  'deal-packet',
  'project-brief',
  'one-pager',
] as const;

/**
 * Union of every `relevanceTagPattern` across every report type's checklist,
 * lower-cased and de-duplicated. This is the closed vocabulary the classifier
 * may emit — emitting a tag outside this set would do nothing useful because
 * the matcher only looks for these patterns.
 *
 * PL4 — also includes INTENT_GATEWAY_TAGS so the Gemini classifier may
 * emit them. Without inclusion in the closed vocabulary the post-filter
 * (`allowed.has(t)`) would strip them out before they reach the doc.
 */
export const ALL_RELEVANCE_TAG_PATTERNS: readonly string[] = (() => {
  const seen = new Set<string>();
  for (const items of Object.values(DOC_CHECKLISTS)) {
    for (const item of items) {
      for (const p of item.relevanceTagPattern) {
        const norm = p.trim().toLowerCase();
        if (norm) seen.add(norm);
      }
    }
  }
  // PL4 — sales-deck-family intent gateway tags.
  for (const t of INTENT_GATEWAY_TAGS) seen.add(t);
  return [...seen].sort();
})();

/**
 * PL4 — filename-based pre-tag for the four obvious sales-deck cases.
 * Skips the Gemini round-trip for well-named files (reduces classifier
 * cost on bulk uploads) and produces a deterministic tag that the
 * downstream substring scan also picks up. Case-insensitive.
 *
 * Patterns deliberately strict — "sales pipeline.csv" must NOT trigger
 * 'sales-deck'. The match is on whole phrases with word boundaries.
 */
const INTENT_GATEWAY_FILENAME_PATTERNS: ReadonlyArray<{ pattern: RegExp; tag: string }> = [
  { pattern: /sales[\s_-]*deck/i, tag: 'sales-deck' },
  { pattern: /deal[\s_-]*packet/i, tag: 'deal-packet' },
  { pattern: /project[\s_-]*brief/i, tag: 'project-brief' },
  { pattern: /one[\s_-]*pager/i, tag: 'one-pager' },
];

export function intentGatewayTagsFromFilename(fileName: string): string[] {
  const out: string[] = [];
  for (const { pattern, tag } of INTENT_GATEWAY_FILENAME_PATTERNS) {
    if (pattern.test(fileName)) out.push(tag);
  }
  return out;
}

/**
 * Returns true when `text` is real extracted content rather than one of the
 * `[Binary file …]` / `[Image …]` / `[Extraction failed …]` placeholders that
 * `extractTextFromFile` emits when text isn't available.
 *
 * DIM-1 — delegates to the shared `hasUsableExtractedText` predicate
 * (`knowledge/extractedTextQuality.ts`), which `evidenceReadiness.
 * computeDocQuality` also uses now. Two behaviour corrections vs the old
 * prefix list: `[Image vision: …]` envelopes with a body ARE usable
 * (real vision extraction), and `[PDF: … — no extractable text]`
 * placeholders are NOT.
 */
export function hasUsableText(text: string | undefined | null): boolean {
  return hasUsableExtractedText(text);
}

/**
 * Merge content-derived tags into a doc's existing tags. Manual / prior tags
 * are preserved; the union is de-duplicated case-insensitively (keeping the
 * first-seen casing) and order-stable (existing tags first).
 */
export function mergeAutoTags(existing: readonly string[] | undefined, additions: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const t of existing ?? []) {
    const norm = t.trim().toLowerCase();
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    out.push(t);
  }
  for (const t of additions) {
    const norm = t.trim().toLowerCase();
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    out.push(norm);
  }
  return out;
}

/* ── Gemini path ──────────────────────────────────────────────────── */

interface GeminiTagResponse {
  tags: string[];
}

/**
 * DH-1 — MIME family used to select a prompt variant. The classifier treats
 * Office spreadsheets, Word docs, and PDFs differently because the *shape* of
 * the extracted text is different, even when the textual content overlaps:
 *
 *   - `pdf` (and any default fallback): narrative document, full prose
 *     visible from page 1 — the legacy prompt works as-is.
 *   - `docx`: extraction often leads with the cover-page / template header
 *     (e.g. "Phase I Environmental Site Assessment — Redhead Industrial").
 *     The structured fields *are* the evidence; tell the model to weigh
 *     them rather than waiting for narrative.
 *   - `xlsx`: extraction is CSV-of-cells; cell labels look like prose but
 *     are not narrative claims ("Zoning: M-1" in a header row does not
 *     mean the spreadsheet is a zoning letter). The model is told to read
 *     tabular data and NOT to infer document intent from row/column labels.
 *   - `image`: deferred to the IIA (Image Insight Annotation) pack — we
 *     return an empty tag list rather than call Gemini text classification,
 *     because the upstream text-extractor already emits an image
 *     placeholder for these (no usable text exists at this layer).
 *   - `image-document` (DIM-1): an image upload whose VISION extraction
 *     reads like an image OF a document (`looksLikeDocumentImageText`)
 *     — a screenshot / scan / photo of a spreadsheet, letter, table….
 *     These carry real transcribed content, so they run the text
 *     classifier with a stanza explaining the text's provenance instead
 *     of short-circuiting to the IIA deferral.
 */
export type DocClassifierMimeFamily = 'pdf' | 'docx' | 'xlsx' | 'image' | 'image-document' | 'default';

/**
 * DH-1 — collapse a raw `file.type` MIME string into one of the variants the
 * prompt selector understands. Falls back to `'default'` (== PDF-style
 * prompt) on anything unrecognised, which keeps behaviour back-compat with
 * pre-DH-1 callers that never passed a `mimeType` at all.
 */
export function mimeFamilyFor(mimeType: string | undefined | null): DocClassifierMimeFamily {
  const m = (mimeType ?? '').toLowerCase().trim();
  if (!m) return 'default';
  if (m === 'application/pdf') return 'pdf';
  if (
    m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    m === 'application/msword'
  ) {
    return 'docx';
  }
  if (
    m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    m === 'application/vnd.ms-excel' ||
    m === 'text/csv'
  ) {
    return 'xlsx';
  }
  if (m.startsWith('image/')) return 'image';
  return 'default';
}

/**
 * DH-1 — per-MIME-family emphasis stanza. Inserted into the prompt right
 * before the intent-gateway definitions so the model conditions on the doc's
 * shape before scoring vocabulary. Strings exported for tests so the
 * `runGeminiJSON` mock can assert the right variant fired.
 */
export const MIME_PROMPT_EMPHASIS: Record<DocClassifierMimeFamily, string> = {
  pdf:
    `DOCUMENT SHAPE — PDF.\n` +
    `Read the document body as narrative prose. Tag a topic only when the ` +
    `document is substantively about it (a Phase I ESA, a zoning letter), ` +
    `not when the topic is merely mentioned.\n\n`,
  docx:
    `DOCUMENT SHAPE — Word document (DOCX).\n` +
    `DOCX extraction frequently leads with the COVER PAGE / HEADER fields ` +
    `("Phase I Environmental Site Assessment — <site name>", "Prepared by ` +
    `<firm>", "Date: 2026-…"). Treat these structured fields as ` +
    `first-class evidence of the document's TYPE — if the cover/header ` +
    `names the document kind, tag it. Do not require narrative confirmation.\n\n`,
  xlsx:
    `DOCUMENT SHAPE — Spreadsheet (XLSX/CSV).\n` +
    `The text below is a TABULAR dump of cells (sheet-by-sheet CSV). Row / ` +
    `column labels frequently look like narrative claims ("Zoning: M-1", ` +
    `"Flood Zone: X"), but a spreadsheet is NEVER a zoning letter or a ` +
    `flood-elevation certificate — those are signed narrative documents. ` +
    `Tag the document's actual KIND (e.g. "financial model", "cost ` +
    `estimate" when in vocabulary), not the topics implied by individual ` +
    `cell labels.\n\n`,
  image:
    // Unused — the `image` family short-circuits before reaching the prompt.
    // Kept for completeness so a future caller that wants to drive a vision
    // model from this module has a stanza to start from.
    `DOCUMENT SHAPE — Image. (Vision classification deferred to IIA pack.)\n\n`,
  'image-document':
    `DOCUMENT SHAPE — Image OF a document (DIM-1).\n` +
    `The text below is a VISION TRANSCRIPTION of an image upload — a ` +
    `screenshot, scan, or photograph of a document (spreadsheet, table, ` +
    `letter, form, report page…). Treat the transcription as the ` +
    `document's own content and tag the document's actual KIND. Ignore ` +
    `the image envelope line and any IMAGE-CONTENT marker; do NOT tag ` +
    `it as site imagery merely because the carrier file is an image.\n\n`,
  default:
    `DOCUMENT SHAPE — Unknown / fallback.\n` +
    `Read the document body conservatively. Tag a topic only when the ` +
    `document is clearly about it.\n\n`,
};

/**
 * DH-1 — assemble the Gemini classifier prompt for the given MIME family.
 * Exported so unit tests can assert the variant text without spying on
 * `runGeminiJSON` internals.
 */
export function buildClassifierPrompt(
  fileName: string,
  text: string,
  family: DocClassifierMimeFamily,
): string {
  const vocab = ALL_RELEVANCE_TAG_PATTERNS.join(', ');
  return (
    `You are tagging a document for a Site Evidence library. The text below is the first ` +
    `2-3 pages of an uploaded document.\n\n` +
    `DOCUMENT FILENAME: ${fileName}\n` +
    `DOCUMENT TEXT (first ${MAX_CLASSIFY_CHARS} chars):\n${text.slice(0, MAX_CLASSIFY_CHARS)}\n\n` +
    // DH-1 — per-MIME emphasis. Must precede the vocabulary instruction
    // so the model conditions on document shape BEFORE scoring tags.
    MIME_PROMPT_EMPHASIS[family] +
    `Choose ALL applicable tags from this exact list (use the strings verbatim, ` +
    `lower-case, no others): [${vocab}]\n\n` +
    `Be conservative — only include a tag if the document is clearly about that topic ` +
    `(e.g. only tag "phase i" / "esa" if this really is a Phase I Environmental Site ` +
    `Assessment, not just any document that mentions the environment). If nothing ` +
    `clearly applies, return an empty array.\n\n` +
    // PL4 — disambiguate the intent-gateway tags so the model recognises
    // sales-style documents and emits the right tag. Without these the
    // model under-tags marketing-style PDFs as 'developer-brief' or
    // similar checklist tags and the intent extractor never fires.
    `INTENT GATEWAY TAG DEFINITIONS (use these only when the doc is clearly ` +
    `the described kind — they activate the wizard's Project Intent ` +
    `auto-suggestion path):\n` +
    `- 'sales-deck' — marketing-style overview with images of the site + ` +
    `headline KPIs (acreage, MW available, time-to-power) + ownership info. ` +
    `Usually slide-deck-style layout.\n` +
    `- 'deal-packet' — bundle of investor materials: term sheet, capex / ` +
    `opex summary, schedule, exit assumptions. Targets buyers / financial ` +
    `partners, not technical reviewers.\n` +
    `- 'project-brief' — one-pager describing project intent + capacity + ` +
    `timing + workload profile. May include a site rendering.\n` +
    `- 'one-pager' — single-page summary of the site / project (not a ` +
    `multi-page report). Often used as a leave-behind.\n\n` +
    `Return ONLY a JSON object: { "tags": ["<tag>", ...] }`
  );
}

async function classifyViaGemini(
  fileName: string,
  text: string,
  family: DocClassifierMimeFamily = 'default',
): Promise<string[]> {
  // DH-1 — image family is deferred to the IIA pack; no Gemini call here.
  // (Defensive — the public entrypoint also short-circuits, so this only
  // fires if a future caller wires the internal helper directly.)
  if (family === 'image') return [];

  const contents = buildClassifierPrompt(fileName, text, family);

  const schema = {
    type: Type.OBJECT,
    properties: {
      tags: { type: Type.ARRAY, items: { type: Type.STRING } },
    },
    required: ['tags'],
  };

  const raw = await runGeminiJSON<GeminiTagResponse>({
    model: 'gemini-2.5-flash',
    contents,
    schema,
    timeoutMs: 30_000,
  });

  const allowed = new Set(ALL_RELEVANCE_TAG_PATTERNS);
  return (raw.tags ?? [])
    .map(t => (typeof t === 'string' ? t.trim().toLowerCase() : ''))
    .filter(t => allowed.has(t))
    .filter((t, i, arr) => arr.indexOf(t) === i);
}

/* ── Deterministic fallback ───────────────────────────────────────── */

/**
 * Substring scan of the document text (plus filename) against the pattern
 * vocabulary. This is what the legacy matcher does — except over the document
 * BODY, not just the filename — so it strictly dominates the pre-AC1 behaviour.
 */
export function classifyViaSubstring(fileName: string, text: string): string[] {
  const haystack = `${fileName}\n${text}`.toLowerCase();
  return ALL_RELEVANCE_TAG_PATTERNS.filter(p => haystack.includes(p));
}

/* ── Public entry point ───────────────────────────────────────────── */

export interface ClassifyContentTagsOptions {
  /**
   * Override for tests — defaults to the Gemini path. Signature widened to
   * receive the resolved MIME family so tests can assert the variant.
   * Existing callers passing the 2-arg signature continue to work (the
   * third argument is optional).
   */
  geminiClassifier?: (fileName: string, text: string, family?: DocClassifierMimeFamily) => Promise<string[]>;
  /** When true, skip Gemini and use the deterministic substring scan. */
  forceFallback?: boolean;
  /**
   * DH-1 — raw MIME type of the source file. Drives prompt-variant
   * selection via `mimeFamilyFor`. Absent / unknown values fall back to
   * the legacy ("default") prompt, so back-compat callers see identical
   * behaviour. `image/*` short-circuits to an empty tag list (deferred to
   * the IIA vision pack).
   */
  mimeType?: string;
}

/**
 * Classify a document's content into checklist-pattern tags. Tries Gemini
 * first, falls back to the substring scan on any failure. Never throws.
 * Returns the matched tags (subset of `ALL_RELEVANCE_TAG_PATTERNS`).
 *
 * DH-1 — when `options.mimeType` is supplied the Gemini prompt switches to a
 * MIME-aware variant (PDF / DOCX / XLSX / Image). Image MIMEs short-circuit
 * to `[]` because vision-based classification is owned by the IIA pack and
 * the text path has nothing useful to say for raster inputs.
 */
export async function classifyDocContentTags(
  fileName: string,
  text: string,
  options: ClassifyContentTagsOptions = {},
): Promise<string[]> {
  let family = mimeFamilyFor(options.mimeType);
  // DH-1 — defer image classification to the IIA pack. Skip even the
  // substring scan: the text body is an `[Image: …]` placeholder by then,
  // and emitting tags off the filename alone is the exact false-positive
  // mode DMS-1 was designed to suppress.
  //
  // DIM-1 exception — an image whose vision extraction is a document
  // transcription (`[Image vision:` envelope + document structure) DOES
  // have classifiable text. Upgrade the family so the prompt explains
  // the text's provenance; everything else about the flow is the
  // standard document path.
  if (family === 'image') {
    if (!looksLikeDocumentImageText(text)) return [];
    family = 'image-document';
  }
  if (!hasUsableText(text)) return [];
  if (options.forceFallback) return classifyViaSubstring(fileName, text);
  const gemini = options.geminiClassifier ?? classifyViaGemini;
  try {
    const tags = await gemini(fileName, text, family);
    // Gemini occasionally returns an empty array for a doc that clearly does
    // match a pattern verbatim (overzealous "be conservative"). Fold in the
    // substring hits so a verbatim mention is never lost — still a subset of
    // the closed vocabulary.
    return mergeAutoTags(tags, classifyViaSubstring(fileName, text));
  } catch (e) {
    console.warn(`[docClassifier] Gemini path failed for "${fileName}", using substring fallback:`, (e as Error)?.message);
    return classifyViaSubstring(fileName, text);
  }
}

/**
 * Idempotently classify a single document and return a NEW document with the
 * content-derived tags merged in and `autoTaggedAt` stamped. Returns the
 * input unchanged (same reference) when:
 *   - the doc already has `autoTaggedAt` (already classified), or
 *   - the doc has no usable `textContent` (binary placeholder etc.).
 *
 * Pure with respect to the input — does not mutate `doc`.
 */
export async function classifyAndMergeDocTags(
  doc: SiteDocument,
  options: ClassifyContentTagsOptions = {},
): Promise<SiteDocument> {
  if (doc.autoTaggedAt) return doc;
  // PL4 — filename pre-tag for the four obvious intent-gateway cases.
  // Runs even when the doc has no usable extracted text (binary KMZ /
  // OCR-pending PDF) so a doc literally named "Redhead Sales Deck.pdf"
  // still triggers `extractIntentFromDocs` later. Deterministic and
  // free — no Gemini call required.
  const filenameTags = intentGatewayTagsFromFilename(doc.fileName);
  if (!hasUsableText(doc.textContent)) {
    // Skip-and-log: a binary upload (KMZ, image, raw PDF that didn't extract)
    // can't be content-classified. But if the filename matches an intent
    // gateway pattern, stamp the gateway tag so intent extraction can run
    // on the doc later (when it gets OCR'd) without waiting for backfill.
    if (filenameTags.length > 0) {
      return {
        ...doc,
        tags: mergeAutoTags(doc.tags, filenameTags),
        autoTaggedAt: new Date().toISOString(),
        autoTagModelVersion: AUTO_TAG_VERSION,
      };
    }
    return doc;
  }
  // DH-1 — thread the doc's MIME type through so the Gemini prompt picks
  // a shape-aware variant (PDF default / DOCX cover-header focus / XLSX
  // tabular-no-narrative-inference). Caller-supplied `options.mimeType`
  // wins so tests can pin a specific variant.
  // P3 — content-index topic summaries lead, then the body's opening.
  const topicLines = (doc.topicSegments ?? []).map(s => `${s.topic}: ${s.summary}`);
  const classifierText = topicLines.length > 0
    ? `CONTENT INDEX:\n${topicLines.slice(0, 40).join('\n')}\n\nBODY:\n${documentBodyText(doc)}`
    : documentBodyText(doc);
  const tags = await classifyDocContentTags(doc.fileName, classifierText, {
    ...options,
    mimeType: options.mimeType ?? doc.mimeType,
  });
  return {
    ...doc,
    // PL4 — union filename-derived intent-gateway tags with the
    // content-derived tags so the well-named files don't lose their
    // gateway tag when Gemini under-tags.
    tags: mergeAutoTags(doc.tags, [...tags, ...filenameTags]),
    autoTaggedAt: new Date().toISOString(),
    autoTagModelVersion: AUTO_TAG_VERSION,
  };
}
